import { useEffect, useState } from 'react';
import { Save, RefreshCcw } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import ErrorBanner from '../components/ErrorBanner.jsx';
import PhoneNumbersEditor from '../components/PhoneNumbersEditor.jsx';
import CallStackCard from '../components/CallStackCard.jsx';
import AccessCodesEditor from '../components/AccessCodesEditor.jsx';
import FlaggedCallersEditor from '../components/FlaggedCallersEditor.jsx';
import KnowledgeBranchesEditor from '../components/KnowledgeBranchesEditor.jsx';
import JoinMeetingCard from '../components/JoinMeetingCard.jsx';
import EngagementSwitch from '../components/EngagementSwitch.jsx';
import PersonasEditor from '../components/PersonasEditor.jsx';
import TenantApiKeysEditor from '../components/TenantApiKeysEditor.jsx';
import EmailRecapsCard from '../components/EmailRecapsCard.jsx';
import TimezoneCard from '../components/TimezoneCard.jsx';
import PartnersCard from '../components/PartnersCard.jsx';
import { settingsAPI } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';

const ROLE_OPTIONS = [
  { value: 'concierge', label: 'Concierge' },
  { value: 'receptionist', label: 'Receptionist' },
  { value: 'sales', label: 'Sales' },
  { value: 'support', label: 'Support' },
  { value: 'custom', label: 'Custom' }
];

const TONE_OPTIONS = [
  'friendly-professional',
  'warm-empathetic',
  'concise-direct',
  'formal',
  'casual'
];

const LANGUAGE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect (mirror the caller)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'pt', label: 'Brazilian Portuguese' },
  { value: 'vi', label: 'Vietnamese' },
  { value: 'zh', label: 'Mandarin Chinese' }
];

const DEFAULTS = {
  companyName: '',
  assistantRole: 'concierge',
  tone: 'friendly-professional',
  customInstructions: '',
  panelInstructions: '',
  temperature: 0.6,
  maxTokens: 80,
  defaultLanguage: 'auto'
};

// Keep in sync with the backend aiConfigSchema cap (settings.js).
const INSTRUCTIONS_MAX = 12000;

export default function Settings() {
  // Executive-Admin tenants get a trimmed Settings page — no owner API keys,
  // call-routing pointer, standalone Join-a-Meeting, or DTMF access codes.
  const { tenant, selectedTenant, isOwner } = useAuth();
  const shownTenant = (isOwner && selectedTenant) || tenant;
  const isExecAdmin = !!shownTenant?.features?.executiveAdmin;

  const [config, setConfig] = useState(DEFAULTS);
  const [original, setOriginal] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const aiRes = await settingsAPI.getAiConfig().catch((e) => {
        if (e.status === 404) return null;
        throw e;
      });
      if (aiRes) {
        const data = aiRes?.data || aiRes || {};
        const merged = { ...DEFAULTS, ...data };
        setConfig(merged);
        setOriginal(merged);
      }
    } catch (err) {
      setError(err.message || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const dirty =
    (config.companyName || '') !== (original.companyName || '') ||
    config.assistantRole !== original.assistantRole ||
    config.tone !== original.tone ||
    config.customInstructions !== original.customInstructions ||
    (config.panelInstructions || '') !== (original.panelInstructions || '') ||
    Number(config.temperature) !== Number(original.temperature) ||
    Number(config.maxTokens) !== Number(original.maxTokens) ||
    (config.defaultLanguage || 'auto') !== (original.defaultLanguage || 'auto');

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = {
        companyName: (config.companyName || '').trim(),
        assistantRole: config.assistantRole,
        tone: config.tone,
        customInstructions: config.customInstructions,
        panelInstructions: config.panelInstructions,
        temperature: Number(config.temperature),
        maxTokens: Number(config.maxTokens),
        defaultLanguage: config.defaultLanguage || 'auto'
      };
      const res = await settingsAPI.updateAiConfig(payload);
      const next = { ...DEFAULTS, ...(res?.data || res || payload) };
      setOriginal(next);
      setConfig(next);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message || 'Failed to save AI configuration.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setConfig(original);

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Settings"
        subtitle="Tune your assistant's behaviour and review your phone numbers"
        actions={
          <button
            type="button"
            onClick={loadAll}
            disabled={loading}
            className="btn-ghost"
          >
            <RefreshCcw size={14} />
            Refresh
          </button>
        }
      />

      {error && (
        <div className="mb-6">
          <ErrorBanner message={error} />
        </div>
      )}

      <EngagementSwitch />

      <section className="card mb-8">
        <h2 className="text-xl text-stone-100 mb-1">Assistant configuration</h2>
        <p className="text-sm text-stone-400 mb-6">
          These settings control how your AI assistant behaves on calls.
        </p>

        <form onSubmit={onSubmit} className="space-y-5">
          <div>
            <label className="field-label" htmlFor="companyName">
              Business name
            </label>
            <input
              id="companyName"
              type="text"
              className="field-input"
              placeholder="e.g. VitaMD.health"
              value={config.companyName || ''}
              onChange={(e) =>
                setConfig({ ...config, companyName: e.target.value })
              }
              disabled={loading || saving}
              maxLength={120}
            />
            <p className="mt-1 text-xs text-stone-500">
              Spoken aloud as your brand — e.g. the greeting "welcome to {config.companyName || 'your business'}".
            </p>
          </div>

          <div>
            <label className="field-label" htmlFor="assistantRole">
              Assistant role
            </label>
            <select
              id="assistantRole"
              className="field-input"
              value={config.assistantRole || ''}
              onChange={(e) =>
                setConfig({ ...config, assistantRole: e.target.value })
              }
              disabled={loading || saving}
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor="tone">
              Tone
            </label>
            <select
              id="tone"
              className="field-input"
              value={config.tone || ''}
              onChange={(e) => setConfig({ ...config, tone: e.target.value })}
              disabled={loading || saving}
            >
              {TONE_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor="defaultLanguage">
              Caller language
            </label>
            <select
              id="defaultLanguage"
              className="field-input"
              value={config.defaultLanguage || 'auto'}
              onChange={(e) =>
                setConfig({ ...config, defaultLanguage: e.target.value })
              }
              disabled={loading || saving}
            >
              {LANGUAGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-stone-500">
              Auto-detect mirrors the caller's language each turn. Pick a single
              language to lock the assistant when callers are uniform.
            </p>
          </div>

          <div>
            <label className="field-label" htmlFor="customInstructions">
              Custom instructions
            </label>
            <textarea
              id="customInstructions"
              className="field-input min-h-[120px] resize-y"
              placeholder="e.g. Always greet callers by name when known. Don't quote prices over the phone — direct callers to a written quote instead."
              value={config.customInstructions || ''}
              onChange={(e) =>
                setConfig({ ...config, customInstructions: e.target.value })
              }
              disabled={loading || saving}
              maxLength={INSTRUCTIONS_MAX}
            />
            <p className="mt-1 text-xs text-stone-500">
              {(config.customInstructions || '').length} / {INSTRUCTIONS_MAX} characters
            </p>
          </div>

          <div>
            <label className="field-label" htmlFor="panelInstructions">
              Panel flow (two/three-AI mode)
            </label>
            <textarea
              id="panelInstructions"
              className="field-input min-h-[120px] resize-y"
              placeholder="Defines who the AI panel personas are and how they talk together. Used when this tenant runs in multi-voice panel mode (engagement = VitaMD Landing)."
              value={config.panelInstructions || ''}
              onChange={(e) =>
                setConfig({ ...config, panelInstructions: e.target.value })
              }
              disabled={loading || saving}
              maxLength={INSTRUCTIONS_MAX}
            />
            <p className="mt-1 text-xs text-stone-500">
              {(config.panelInstructions || '').length} / {INSTRUCTIONS_MAX} characters · only used in multi-voice panel mode
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="field-label" htmlFor="temperature">
                Temperature
                <span className="text-stone-500 font-normal ml-2">
                  ({Number(config.temperature || 0).toFixed(2)})
                </span>
              </label>
              <input
                id="temperature"
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={config.temperature ?? 0.6}
                onChange={(e) =>
                  setConfig({ ...config, temperature: Number(e.target.value) })
                }
                disabled={loading || saving}
                className="w-full accent-alfred-gold"
              />
              <p className="text-xs text-stone-500">
                Lower = more predictable, higher = more creative.
              </p>
            </div>

            <div>
              <label className="field-label" htmlFor="maxTokens">
                Max tokens per response
              </label>
              <input
                id="maxTokens"
                type="number"
                min="20"
                max="2000"
                step="10"
                className="field-input"
                value={config.maxTokens ?? 80}
                onChange={(e) =>
                  setConfig({ ...config, maxTokens: Number(e.target.value) })
                }
                disabled={loading || saving}
              />
              <p className="mt-1 text-xs text-stone-500">
                Caps the length of each assistant turn.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              className="btn-primary"
              disabled={!dirty || saving || loading}
            >
              <Save size={16} />
              {saving ? 'Saving…' : 'Save changes'}
            </button>
            {dirty && !saving && (
              <button type="button" className="btn-ghost" onClick={reset}>
                Reset
              </button>
            )}
            {savedAt && !dirty && (
              <span className="text-xs text-stone-500">
                Saved {savedAt.toLocaleTimeString()}
              </span>
            )}
          </div>
        </form>
      </section>

      {/* Above the recap card on purpose: the recap has its own send-time zone,
          and this is the account-wide clock those times should be read against. */}
      <TimezoneCard />

      <EmailRecapsCard />

      {!isExecAdmin && <TenantApiKeysEditor />}

      <div className="mb-8">
        <PersonasEditor />
      </div>

      {!isExecAdmin && (
        <div className="mb-8 rounded-xl border border-stone-800 bg-stone-900/40 p-4">
          <p className="text-sm text-stone-400">
            Call handling (voicemail, forwarding, take-a-message, scheduling) and the
            AI's answer material (Business Info, FAQ, Knowledge) now live under{' '}
            <span className="text-alfred-gold">Call Knowledge</span> in the sidebar.
          </p>
        </div>
      )}

      <div className="mb-8">
        <PhoneNumbersEditor hideActions={isExecAdmin} />
      </div>

      {!isExecAdmin && <CallStackCard />}

      {/* Owner-only (the card renders nothing otherwise): who may dial from the sales
          portal on this account's line. Sits under the numbers because it is about one. */}
      {!isExecAdmin && <PartnersCard />}

      {!isExecAdmin && (
        <div className="mb-8">
          <JoinMeetingCard />
        </div>
      )}

      {/* The care line moved to its own page (/outbound-calls) in v1.49.0. It is a
          schedule plus today's state, not a setting — and two save surfaces over the
          same row is how a config screen and a live screen start disagreeing. */}

      {/* The admin review the auto-block policy assumes exists. Without it a caller
          the panel turned away is invisible and unrecoverable. */}
      {!isExecAdmin && (
        <div className="mb-8">
          <FlaggedCallersEditor />
        </div>
      )}

      {!isExecAdmin && <AccessCodesEditor />}

      {!isExecAdmin && <KnowledgeBranchesEditor />}
    </div>
  );
}
